/**
 * TanStack Query hooks — the React surface for talking to the backend.
 *
 * Each hook returns the standard `{ data, isLoading, error, ... }` shape so
 * components stay simple. Server state is cached for 30 s by default
 * (configured in main.tsx).
 */
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, setToken } from "./client";

// ---- Types (shape matches the FastAPI Pydantic responses) ----

export interface ProductListItem {
  id: string;
  slug: string;
  name: string;
  brand: string;
  category_slug: string;
  category_name: string;
  sub_category: string | null;
  stock_status: "in_stock" | "low_stock" | "out_of_stock" | "discontinued";
  rating: number;
  reviews: number;
  price_min: number;
  price_max: number;
  is_featured: boolean;
  badge: string | null;
  delivery_time_mins: number | null;
  unit_label: string;
  schedule: "NONE" | "H" | "H1" | "X";
}

export interface ProductPriceRow {
  role: "customer" | "clinician" | "retailer";
  mrp: number;
  selling_price: number;
  currency: string;
}

export interface ProductVariant {
  id: string;
  pack_size: string;
  unit_label: string;
  barcode: string | null;
  is_default: boolean;
  prices: ProductPriceRow[];
}

export interface ProductDetail extends ProductListItem {
  description: string | null;
  hsn_code: string | null;
  specs: { label: string; value: string }[];
  variants: ProductVariant[];
  images: { storage_key: string; alt: string | null; is_primary: boolean; sort_order: number }[];
}

export interface CategoryItem {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  sku_prefix: string;
  glyph: string | null;
  image_key: string | null;
  sort_order: number;
  is_active: boolean;
  product_count: number;
}

export interface Paginated<T> {
  total: number;
  limit: number;
  offset: number;
  items: T[];
}

export interface MeResponse {
  id: string;
  email: string;
  phone: string | null;
  role: "customer" | "clinician" | "retailer" | "sales" | "manager" | "admin";
  status: "pending_kyc" | "active" | "suspended";
  email_verified: boolean;
  last_login_at: string | null;
  profile: { full_name: string; avatar_url: string | null; attributes: Record<string, unknown> } | null;
  permissions: string[];
}

// ---- Queries ----

export type ProductSort =
  | "relevance"
  | "price-asc"
  | "price-desc"
  | "rating"
  | "popular";

export interface ProductListParams {
  q?: string;
  category?: string;
  sub_category?: string;
  /** Comma-separated brand list. Frontend joins `Array.from(set).join(",")`. */
  brand?: string;
  schedule?: "H" | "H1" | "X";
  in_stock?: boolean;
  featured?: boolean;
  sort?: ProductSort;
  limit?: number;
  offset?: number;
}

export interface ReviewAuthor {
  id: string;
  full_name: string;
  role: "customer" | "clinician" | "retailer" | "sales" | "manager" | "admin";
}

export interface ReviewOut {
  id: string;
  rating: number;
  title: string | null;
  body: string | null;
  is_verified_purchase: boolean;
  created_at: string;
  updated_at: string;
  author: ReviewAuthor;
}

export interface ReviewCreate {
  rating: number;
  title?: string | null;
  body?: string | null;
}

export function useProducts(params: ProductListParams = {}) {
  return useQuery<Paginated<ProductListItem>>({
    queryKey: ["products", params],
    queryFn: () => api.products.list(params),
  });
}

export function useProduct(slug: string | undefined) {
  return useQuery<ProductDetail>({
    queryKey: ["product", slug],
    queryFn: () => api.products.get(slug!),
    enabled: !!slug,
  });
}

export function useCategories() {
  return useQuery<Paginated<CategoryItem>>({
    queryKey: ["categories"],
    queryFn: () => api.categories.list(),
  });
}

export function useCategory(slug: string | undefined) {
  return useQuery<CategoryItem>({
    queryKey: ["category", slug],
    queryFn: () => api.categories.get(slug!),
    enabled: !!slug,
  });
}

export function useMe() {
  return useQuery<MeResponse | null>({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api.auth.me();
      } catch (e: any) {
        if (e?.status === 401) return null;
        throw e;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

/** Fetch multiple products by slug in parallel — used by the Compare page. */
export function useProductsBySlugs(slugs: string[]) {
  return useQueries({
    queries: slugs.map((slug) => ({
      queryKey: ["product", slug],
      queryFn: () => api.products.get(slug) as Promise<ProductDetail>,
      enabled: !!slug,
    })),
  });
}

export function useReviews(
  slug: string | undefined,
  params: { limit?: number; offset?: number } = {}
) {
  return useQuery<Paginated<ReviewOut>>({
    queryKey: ["reviews", slug, params],
    queryFn: () => api.products.reviews(slug!, params),
    enabled: !!slug,
  });
}

export function useCreateReview(slug: string | undefined) {
  const qc = useQueryClient();
  return useMutation<ReviewOut, any, ReviewCreate>({
    mutationFn: (payload) => api.products.postReview(slug!, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reviews", slug] });
      qc.invalidateQueries({ queryKey: ["product", slug] });
    },
  });
}

// ---- Mutations ----

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { email: string; password: string }) => api.auth.login(vars),
    onSuccess: async (data: any) => {
      setToken(data.access_token);
      try {
        window.localStorage.setItem("nethrasap.refresh", data.refresh_token);
      } catch (_) {}
      // Claim any anonymous cart for the freshly-logged-in user.
      const userId = data?.user?.id;
      if (userId && (api as any).cart?.claim) {
        try { await (api as any).cart.claim({ user_id: userId }); } catch (_) {}
      }
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["cart"] });
    },
  });
}

export function useSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      role: "customer" | "clinician" | "retailer";
      email: string;
      password: string;
      name: string;
      phone?: string;
    }) => api.auth.signup(vars),
    onSuccess: async (data: any) => {
      setToken(data.access_token);
      try {
        window.localStorage.setItem("nethrasap.refresh", data.refresh_token);
      } catch (_) {}
      const userId = data?.user?.id;
      if (userId && (api as any).cart?.claim) {
        try { await (api as any).cart.claim({ user_id: userId }); } catch (_) {}
      }
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["cart"] });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const refresh = window.localStorage.getItem("nethrasap.refresh");
      if (refresh) {
        try { await api.auth.logout(refresh); } catch (_) {}
      }
      if ((api as any).cart?.release) {
        try { await (api as any).cart.release(); } catch (_) {}
      }
    },
    onSettled: () => {
      setToken(null);
      try { window.localStorage.removeItem("nethrasap.refresh"); } catch (_) {}
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["cart"] });
    },
  });
}

// ---- Cart hooks ----

export interface CartLine {
  id: string;
  product_slug: string;
  product_name: string;
  product_brand: string;
  product_unit_label: string;
  product_stock_status: "in_stock" | "low_stock" | "out_of_stock" | "discontinued";
  product_badge: string | null;
  product_delivery_time_mins: number | null;
  product_image_key: string;
  qty: number;
  unit_price: number;
  line_total: number;
}

export interface CartResponse {
  items: CartLine[];
  coupon: { code: string; discount: number } | null;
  subtotal: number;
  gst_amount: number;
  shipping: number;
  discount: number;
  total: number;
  item_count: number;
  owner: string;
}

const cartApi = () => (api as any).cart;

export function useCart() {
  return useQuery<CartResponse>({
    queryKey: ["cart"],
    queryFn: () => cartApi().get(),
    staleTime: 5_000,
  });
}

export function useCartCount(): number {
  const { data } = useCart();
  return data?.item_count ?? 0;
}

export function useAddCartItem() {
  const qc = useQueryClient();
  return useMutation<CartResponse, any, { slug: string; qty?: number }>({
    mutationFn: (vars) => cartApi().add(vars),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["cart"] }); },
  });
}

export function useUpdateCartItem() {
  const qc = useQueryClient();
  return useMutation<CartResponse, any, { item_id: string; qty: number }>({
    mutationFn: (vars) => cartApi().update(vars),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["cart"] }); },
  });
}

export function useRemoveCartItem() {
  const qc = useQueryClient();
  return useMutation<CartResponse, any, { item_id: string }>({
    mutationFn: (vars) => cartApi().remove(vars),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["cart"] }); },
  });
}

export function useApplyCoupon() {
  const qc = useQueryClient();
  return useMutation<CartResponse, any, { code: string }>({
    mutationFn: (vars) => cartApi().applyCoupon(vars),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["cart"] }); },
  });
}

export function useRemoveCoupon() {
  const qc = useQueryClient();
  return useMutation<CartResponse, any, void>({
    mutationFn: () => cartApi().removeCoupon(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["cart"] }); },
  });
}

export function useClearCart() {
  const qc = useQueryClient();
  return useMutation<CartResponse, any, void>({
    mutationFn: () => cartApi().clear(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["cart"] }); },
  });
}

// ---- Checkout hooks ----

export interface CheckoutPayload {
  address_id: string;
  delivery_method: "standard" | "express" | "cold_chain";
  payment_method: "cod" | "upi" | "card" | "netbank" | "credit";
  notes?: string;
}

export interface CheckoutQuoteResponse extends CartResponse {
  address: any;
  delivery_method: string;
  payment_method: string;
}

const checkoutApi = () => (api as any).checkout;

export function useCheckoutQuote(payload: Partial<CheckoutPayload> | null) {
  return useQuery<CheckoutQuoteResponse>({
    queryKey: ["checkout", "quote", payload],
    queryFn: () => checkoutApi().quote(payload),
    enabled: !!payload && !!payload.address_id,
  });
}

export function usePlaceOrder() {
  const qc = useQueryClient();
  return useMutation<any, any, CheckoutPayload>({
    mutationFn: (vars) => checkoutApi().place(vars),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cart"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

// ---- Orders hooks ----

export interface OrderLineItem {
  product_slug: string;
  product_name: string;
  product_brand: string;
  product_image_key: string;
  unit_label: string;
  qty: number;
  unit_price: number;
  line_total: number;
}

export interface OrderTimelineEntry {
  code: string;
  label: string;
  ts: string;
  done: boolean;
  note?: string;
}

export interface OrderResponse {
  number: string;
  placed_at: string;
  user_id: string;
  status: "placed" | "packed" | "shipped" | "out_for_delivery" | "delivered" | "cancelled";
  items: OrderLineItem[];
  address: { full_name: string; line1: string; line2?: string; city: string; state: string; pin: string; phone: string };
  payment_method: string;
  payment_status: string;
  delivery_method: string;
  notes: string | null;
  coupon_code: string | null;
  subtotal: number;
  gst_amount: number;
  shipping: number;
  discount: number;
  total: number;
  cancelled_at: string | null;
  courier: { name: string; awb: string; rider: string; rider_phone: string };
  timeline: OrderTimelineEntry[];
  eta: string;
}

const ordersApi = () => (api as any).orders;

export function useOrders(params: { status?: string; q?: string; limit?: number; offset?: number } = {}) {
  return useQuery<Paginated<OrderResponse>>({
    queryKey: ["orders", params],
    queryFn: () => ordersApi().list(params),
  });
}

export function useOrder(number: string | undefined | null) {
  return useQuery<OrderResponse>({
    queryKey: ["order", number],
    queryFn: () => ordersApi().get(number!),
    enabled: !!number,
  });
}

export function useTrackOrder(number: string | undefined | null) {
  return useQuery<OrderResponse>({
    queryKey: ["order", "track", number],
    queryFn: () => ordersApi().track(number!),
    enabled: !!number,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });
}

export function useCancelOrder() {
  const qc = useQueryClient();
  return useMutation<OrderResponse, any, { number: string; reason?: string }>({
    mutationFn: ({ number, reason }) => ordersApi().cancel(number, { reason }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["order", vars.number] });
      qc.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

export function useReorder() {
  const qc = useQueryClient();
  return useMutation<any, any, { number: string }>({
    mutationFn: ({ number }) => ordersApi().reorder(number),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["cart"] }); },
  });
}

// ---- Profile + addresses (M2) ----

export interface Profile {
  id: string;
  email: string;
  full_name: string;
  phone: string | null;
  dob: string | null;
  role: string;
  status: string;
  avatar_data_url: string | null;
  permissions: string[];
  email_verified: boolean;
  attributes: Record<string, any>;
}

export interface MockAddress {
  id: string;
  label: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  pin: string;
  phone: string;
  default?: boolean;
  type?: string;
}

const profileApi = () => (api as any).profile;

export function useProfile() {
  return useQuery<Profile>({
    queryKey: ["profile"],
    queryFn: () => profileApi().get(),
    staleTime: 60_000,
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation<Profile, any, Partial<Profile>>({
    mutationFn: (patch) => profileApi().update(patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile"] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useAddresses() {
  return useQuery<MockAddress[]>({
    queryKey: ["addresses"],
    queryFn: () => profileApi().addresses.list(),
  });
}

export function useCreateAddress() {
  const qc = useQueryClient();
  return useMutation<MockAddress, any, Omit<MockAddress, "id">>({
    mutationFn: (payload) => profileApi().addresses.create(payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["addresses"] }); },
  });
}

export function useUpdateAddress() {
  const qc = useQueryClient();
  return useMutation<MockAddress, any, { id: string; patch: Partial<MockAddress> }>({
    mutationFn: ({ id, patch }) => profileApi().addresses.update(id, patch),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["addresses"] }); },
  });
}

export function useDeleteAddress() {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, any, { id: string }>({
    mutationFn: ({ id }) => profileApi().addresses.delete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["addresses"] }); },
  });
}

export function useSetDefaultAddress() {
  const qc = useQueryClient();
  return useMutation<MockAddress, any, { id: string }>({
    mutationFn: ({ id }) => profileApi().addresses.setDefault(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["addresses"] }); },
  });
}

// ---- Enquiries (M2) ----

export interface EnquiryThreadEntry {
  id: string;
  author: string;
  role: string;
  message: string;
  ts: string;
}

export interface EnquiryLine {
  product_slug: string;
  product_name: string;
  qty: number;
  unit_price: number;
  line_total: number;
}

export interface Enquiry {
  id: string;
  user_id: string;
  user_email: string;
  status: "pending" | "quoted" | "confirmed" | "rejected" | "closed";
  placed_at: string;
  items: EnquiryLine[];
  sales_rep: string | null;
  notes: string | null;
  subtotal: number;
  tax: number;
  total: number;
  thread: EnquiryThreadEntry[];
  closed_at?: string;
  confirmed_at?: string;
}

const enquiriesApi = () => (api as any).enquiries;

export function useEnquiries(params: { status?: string; q?: string; limit?: number; offset?: number } = {}) {
  return useQuery<Paginated<Enquiry>>({
    queryKey: ["enquiries", params],
    queryFn: () => enquiriesApi().list(params),
  });
}

export function useEnquiry(id: string | undefined | null) {
  return useQuery<Enquiry>({
    queryKey: ["enquiry", id],
    queryFn: () => enquiriesApi().get(id!),
    enabled: !!id,
  });
}

export function useCreateEnquiry() {
  const qc = useQueryClient();
  return useMutation<Enquiry, any, { items: Array<{ product_slug: string; product_name?: string; qty: number; unit_price?: number }>; notes?: string }>({
    mutationFn: (payload) => enquiriesApi().create(payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["enquiries"] }); },
  });
}

export function useReplyEnquiry() {
  const qc = useQueryClient();
  return useMutation<Enquiry, any, { id: string; message: string }>({
    mutationFn: ({ id, message }) => enquiriesApi().reply(id, { message }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["enquiry", vars.id] });
      qc.invalidateQueries({ queryKey: ["enquiries"] });
    },
  });
}

export function useCloseEnquiry() {
  const qc = useQueryClient();
  return useMutation<Enquiry, any, { id: string }>({
    mutationFn: ({ id }) => enquiriesApi().close(id),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["enquiry", vars.id] });
      qc.invalidateQueries({ queryKey: ["enquiries"] });
    },
  });
}

export function useAcceptQuote() {
  const qc = useQueryClient();
  return useMutation<Enquiry, any, { id: string }>({
    mutationFn: ({ id }) => enquiriesApi().acceptQuote(id),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["enquiry", vars.id] });
      qc.invalidateQueries({ queryKey: ["enquiries"] });
      qc.invalidateQueries({ queryKey: ["cart"] });
    },
  });
}

// ---- Notifications (M2) ----

export interface Notification {
  id: string;
  type: "order" | "enquiry" | "system" | "alert" | "verification" | "report" | "message";
  title: string;
  body: string;
  read: boolean;
  link: string | null;
  created_at: string;
  priority: "low" | "med" | "high";
}

const notificationsApi = () => (api as any).notifications;

export function useNotifications() {
  return useQuery<Notification[]>({
    queryKey: ["notifications"],
    queryFn: () => notificationsApi().list(),
    staleTime: 10_000,
  });
}

export function useUnreadNotificationCount(): number {
  const { data } = useNotifications();
  return (data || []).filter((n) => !n.read).length;
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation<any, any, { id: string }>({
    mutationFn: ({ id }) => notificationsApi().markRead(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["notifications"] }); },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation<any, any, void>({
    mutationFn: () => notificationsApi().markAllRead(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["notifications"] }); },
  });
}

export function useDeleteNotification() {
  const qc = useQueryClient();
  return useMutation<any, any, { id: string }>({
    mutationFn: ({ id }) => notificationsApi().delete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["notifications"] }); },
  });
}

// ---- Enquiries sales mutations (M3) ----

export function useQuoteEnquiry() {
  const qc = useQueryClient();
  return useMutation<Enquiry, any, { id: string; items: Array<{ product_slug: string; unit_price: number }>; message?: string }>({
    mutationFn: ({ id, items, message }) => enquiriesApi().quote(id, { items, message }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["enquiry", vars.id] });
      qc.invalidateQueries({ queryKey: ["enquiries"] });
    },
  });
}

export function useAssignEnquiry() {
  const qc = useQueryClient();
  return useMutation<Enquiry, any, { id: string; rep_id?: string; rep_name?: string }>({
    mutationFn: ({ id, rep_id, rep_name }) => enquiriesApi().assignRep(id, { rep_id, rep_name }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["enquiry", vars.id] });
      qc.invalidateQueries({ queryKey: ["enquiries"] });
    },
  });
}

export function useRejectEnquiry() {
  const qc = useQueryClient();
  return useMutation<Enquiry, any, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => enquiriesApi().rejectEnquiry(id, { reason }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["enquiry", vars.id] });
      qc.invalidateQueries({ queryKey: ["enquiries"] });
    },
  });
}

